import type { SourcingEventDto } from "../../../../autogen/bff-api";
import type { WizardStepLayout } from "../../../../common/agent/pageEntityNames";
import { isAnnouncedPartI } from "../../../../common/procurement/foaPartDisplay";
import { isWithoutCompetition } from "../../../../common/procurement/noCompetition";
import {
  asksTenderValidity,
  basisFieldsAsked,
  hasRequestStage,
  inheritsScopeFromScheme,
  signsItsOwnContract,
} from "../../../../common/procurement/procedure";
import type { PublicEventStep, WizardStep } from "../../../../common/sourcing-events/buyer/wizardSteps";

export interface StepFieldsContext {
  event: SourcingEventDto;
  runsPublicProcurements: boolean;
  hasProjectAccess: boolean;
}

export interface StepField {
  labelKey?: string;
  paths: readonly string[];
  shownWhen?: (context: StepFieldsContext) => boolean;
}

const publicContextOf = ({ event }: StepFieldsContext) =>
  event.eventContext.type === "Public" ? event.eventContext : null;

const procedureTypeOf = (context: StepFieldsContext) => publicContextOf(context)?.procedureType;

const ownsScope = (context: StepFieldsContext) => !inheritsScopeFromScheme(publicContextOf(context));

const runsTimeline = (context: StepFieldsContext) => !isWithoutCompetition(procedureTypeOf(context));

const ASKED_ON_THE_STEP_ITSELF: StepField = { paths: [] };

export const STEP_FIELDS: Record<PublicEventStep, readonly StepField[]> = {
  info: [
    { labelKey: "Title", paths: ["eventData.title"] },
    { labelKey: "Department", paths: [] },
    { labelKey: "Responsible buyer", paths: [] },
    {
      labelKey: "Estimated value",
      paths: ["eventContext.estimatedValue"],
      shownWhen: (context) => basisFieldsAsked(context.runsPublicProcurements, publicContextOf(context)).estimatedValue,
    },
    {
      labelKey: "Basis for the estimated value (internal)",
      paths: ["publicContext.estimatedValueBasis"],
      shownWhen: (context) => context.runsPublicProcurements && publicContextOf(context) != null && ownsScope(context),
    },
    {
      labelKey: "Contract type",
      paths: ["eventContext.contractNature"],
      shownWhen: (context) => basisFieldsAsked(context.runsPublicProcurements, publicContextOf(context)).contractType,
    },
    {
      labelKey: "What does this procurement ask for?",
      paths: ["eventData.includeProducts", "eventData.includeMinimumRequirements", "eventData.includeAwardCriteria"],
      shownWhen: ({ event }) => event.eventContext.type === "Private",
    },
  ],
  classification: [
    { labelKey: "Description", paths: ["eventData.description"] },
    { labelKey: "CPV codes", paths: ["publicContext.cpvCodes"], shownWhen: ownsScope },
    {
      labelKey: "Contract period",
      paths: ["eventData.contractDuration"],
      shownWhen: (context) => signsItsOwnContract(procedureTypeOf(context)),
    },
    {
      labelKey: "Contract options",
      paths: ["eventData.options"],
      shownWhen: (context) => signsItsOwnContract(procedureTypeOf(context)),
    },
    {
      labelKey: "Framework agreement",
      paths: ["publicContext.frameworkAgreement"],
      shownWhen: (context) => signsItsOwnContract(procedureTypeOf(context)),
    },
    {
      labelKey: "Advanced settings",
      paths: [
        "publicContext.euFunded",
        "publicContext.gpaCoverage",
        "publicContext.reservedParticipation",
        "publicContext.reservedExecution",
      ],
    },
  ],
  procedure: [
    {
      labelKey: "Procedure type",
      paths: ["publicContext.procedureType"],
      shownWhen: (context) => !isAnnouncedPartI(publicContextOf(context)),
    },
    {
      labelKey: "Place of performance",
      paths: ["publicContext.placeOfPerformance"],
      shownWhen: (context) => isAnnouncedPartI(publicContextOf(context)),
    },
    {
      labelKey: "Tender validity period",
      paths: ["publicContext.eFormsNoticeData.tenderValidityMonths"],
      shownWhen: (context) => asksTenderValidity(publicContextOf(context)),
    },
    {
      labelKey: "Deadline for requests to participate",
      paths: ["eventData.requestDeadline"],
      shownWhen: (context) => runsTimeline(context) && hasRequestStage(publicContextOf(context)),
    },
    {
      labelKey: "Question deadline",
      paths: ["eventData.milestonePlan.questionDaysBeforeBid"],
      shownWhen: runsTimeline,
    },
    {
      labelKey: "Bid deadline",
      paths: ["eventData.bidSubmissionDeadline", "eventData.milestonePlan.tenderPeriodOffsetDays"],
      shownWhen: runsTimeline,
    },
    {
      labelKey: "Submission language",
      paths: ["publicContext.eFormsNoticeData.submissionLanguages"],
      shownWhen: runsTimeline,
    },
  ],
  qualification: [{ labelKey: "Qualification requirements", paths: ["eventData.selectionCriteria"] }],
  awardCriteria: [{ paths: ["eventData.awardCriteria"] }],
  minimumRequirements: [{ labelKey: "Minimum requirements", paths: ["eventData.minimumRequirements"] }],
  documents: [ASKED_ON_THE_STEP_ITSELF],
  summary: [],
  details: [
    { labelKey: "Description", paths: ["eventData.description"] },
    { labelKey: "Reference id", paths: ["eventData.referenceId"] },
    { labelKey: "Deadline", paths: ["eventData.bidSubmissionDeadline"] },
    { labelKey: "Projects", paths: ["eventData.projects"], shownWhen: (context) => context.hasProjectAccess },
    {
      labelKey: "Contract duration",
      paths: ["eventData.contractDuration"],
      shownWhen: ({ event }) => event.eventContext.type === "MiniCompetition",
    },
  ],
  products: [ASKED_ON_THE_STEP_ITSELF],
  invitations: [{ paths: ["privateContext.organizationInvites"] }],
  reviewAndPublish: [],
};

export const fieldsShownOn = (step: PublicEventStep, context: StepFieldsContext): readonly StepField[] =>
  STEP_FIELDS[step].filter((field) => field.shownWhen?.(context) ?? true);

export const wizardLayout = (
  steps: ReadonlyArray<WizardStep>,
  context: StepFieldsContext,
  stepLabel: (key: PublicEventStep) => string,
  translate: (key: string) => string,
): WizardStepLayout[] =>
  steps.map(({ key }) => {
    const label = stepLabel(key);
    return {
      key,
      label,
      fields: fieldsShownOn(key, context).map((field) => ({
        label: field.labelKey ? translate(field.labelKey) : label,
        paths: field.paths,
      })),
    };
  });
